import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import type { GuestRsvpManageData } from '@fellowship/shared'

type ManageError = Error & { status?: number }

async function fetchManageRsvp(token: string): Promise<GuestRsvpManageData> {
  const res = await fetch(`/api/v1/rsvps/manage/${token}`)
  if (res.status === 410) {
    const err: ManageError = new Error('This manage link has expired')
    err.status = 410
    throw err
  }
  if (res.status === 404) {
    const err: ManageError = new Error('RSVP not found')
    err.status = 404
    throw err
  }
  if (!res.ok) throw new Error('Failed to load RSVP')
  const json = await res.json()
  return json.data as GuestRsvpManageData
}

export function useGuestRsvpManage(token: string) {
  return useQuery<GuestRsvpManageData, ManageError>({
    queryKey: ['guest-rsvp-manage', token],
    queryFn: () => fetchManageRsvp(token),
    retry: false,
  })
}

export function useUpdateGuestRsvp(token: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (data: {
      attendanceStatus?: string
      dishCategory?: string
      dishDescription?: string | null
    }) => {
      const res = await fetch(`/api/v1/rsvps/manage/${token}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(data),
      })
      if (!res.ok) {
        const json = await res.json()
        throw new Error(json.error?.message ?? 'Update failed')
      }
      const json = await res.json()
      return json.data as GuestRsvpManageData
    },
    onSuccess: (data) => {
      queryClient.setQueryData(['guest-rsvp-manage', token], data)
    },
  })
}

export function useCancelGuestRsvp(token: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async () => {
      const res = await fetch(`/api/v1/rsvps/manage/${token}`, { method: 'DELETE' })
      if (!res.ok) {
        const json = await res.json()
        throw new Error(json.error?.message ?? 'Cancel failed')
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['guest-rsvp-manage', token] })
    },
  })
}
